// src/offline/outbox.js
//
// Coda dei rapportini compilati senza rete.
//
// Un rapportino compilato sul campo attraversa tre passi lato server:
// creazione della lavorazione, chiusura del rapportino, invio della mail.
// Offline nessuno dei tre e' possibile, quindi il rapportino resta qui e
// viene spedito quando la rete torna.
//
// Il codice interno lo assegna il server quando riceve i dati, non prima:
// il rapportino resta in coda senza numero e lo riceve alla consegna.
//
// Ogni passo riuscito viene segnato, e la creazione della lavorazione porta
// un identificativo generato qui: se l'invio riesce ma la risposta si perde,
// il tentativo successivo non crea una seconda lavorazione con un secondo
// codice interno.

import { ARCHIVI, scrivi, tutti, elimina, conta, disponibile } from './db.js';
import { authFetch } from '../utils/authFetch.js';
import { generateManutenzionePdfBlob, generateVitPdfBlob } from '../pdf/clientPdf.js';
import { registraGas } from '../services/gasUsage.js';

export const TIPI = { MANUTENZIONE: 'manutenzione', VIT: 'vit' };

const MAX_TENTATIVI = 5;

function nuovoId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `loc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ---------- osservatori, per il contatore nell'interfaccia ---------- */

const osservatori = new Set();

export function osservaCoda(fn) {
  osservatori.add(fn);
  contaInAttesa().then(fn).catch(() => {});
  return () => osservatori.delete(fn);
}

async function avvisa() {
  const n = await contaInAttesa().catch(() => 0);
  osservatori.forEach((fn) => {
    try {
      fn(n);
    } catch (_) {}
  });
}

export async function contaInAttesa() {
  if (!disponibile()) return 0;
  return conta(ARCHIVI.OUTBOX).catch(() => 0);
}

export const elencoInAttesa = () =>
  disponibile() ? tutti(ARCHIVI.OUTBOX).catch(() => []) : Promise.resolve([]);

/* ---------- accodamento ---------- */

/**
 * Mette un rapportino in coda.
 *
 * @param {Object} p
 * @param {string} p.tipo             TIPI.MANUTENZIONE o TIPI.VIT
 * @param {Object} p.datiIniziali     corpo per la rotta init
 * @param {Object} p.datiCompletamento corpo per la rotta complete
 * @param {Blob=} p.pdf               il PDF gia' generato, da allegare alla mail
 * @param {Object=} p.gas             gas usato e recuperato, da registrare all'invio
 * @param {string=} p.workOrderId     se la lavorazione esiste gia'
 * @param {Object=} p.rotte           rotte diverse da quelle standard (rapportini
 *                                    gia' assegnati: niente init, e la chiusura
 *                                    passa da complete-assigned)
 */
export async function accoda({
  tipo,
  datiIniziali,
  datiCompletamento,
  pdf = null,
  gas = null,
  workOrderId = null,
  rotte = null,
}) {
  const voce = {
    id: nuovoId(),
    tipo,
    creatoIl: new Date().toISOString(),
    stato: 'in_attesa',
    tentativi: 0,
    ultimoErrore: null,
    datiIniziali,
    datiCompletamento,
    pdf,
    gas,
    // passi gia' portati a termine, per non ripeterli
    fatto: { init: Boolean(workOrderId), complete: false, gas: false, report: false },
    workOrderId,
    rotte,
  };

  await scrivi(ARCHIVI.OUTBOX, voce);
  await avvisa();
  return voce.id;
}

/* ---------- invio ---------- */

const ROTTE = {
  [TIPI.MANUTENZIONE]: {
    init: '/api/work/maintenance/init',
    complete: '/api/work/maintenance/complete',
    report: '/api/manutenzione/invia-report',
  },
  [TIPI.VIT]: {
    init: '/api/work/vit/init',
    complete: '/api/work/vit/complete',
    report: '/api/vit/invia-report',
  },
};

async function posta(url, corpo) {
  const risposta = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  });

  if (!risposta) throw new Error('Sessione scaduta');

  if (!risposta.ok) {
    const dati = await risposta.json().catch(() => null);
    const errore = new Error(dati?.error || `Errore ${risposta.status}`);
    // 4xx: rimandare non serve, il server rifiuta i dati cosi' come sono
    errore.definitivo = risposta.status >= 400 && risposta.status < 500;
    throw errore;
  }

  return risposta.json().catch(() => ({}));
}

async function blobInBase64(blob) {
  const byte = new Uint8Array(await blob.arrayBuffer());
  const blocco = 0x8000;
  let binario = '';
  for (let i = 0; i < byte.length; i += blocco) {
    binario += String.fromCharCode.apply(null, byte.subarray(i, i + blocco));
  }
  return btoa(binario);
}

/**
 * Il PDF da allegare alla mail, con il codice interno che il server ha
 * appena assegnato. Se la rigenerazione fallisce si ripiega su quello
 * salvato al momento della compilazione: meglio un verbale senza codice
 * che nessun verbale.
 */
async function pdfPerLaMail(voce) {
  const d = voce.datiCompletamento || {};

  const parametri = {
    template: d.template,
    formData: d.formData || {},
    tecnicoSignature: d.tecnicoSignature || null,
    clienteSignature: d.clienteSignature || null,
    inviaReport: false, // la mail la stiamo mandando noi, qui sotto
  };

  try {
    return voce.tipo === TIPI.VIT
      ? await generateVitPdfBlob(parametri)
      : await generateManutenzionePdfBlob({ ...parametri, checks: d.checks || {} });
  } catch (err) {
    console.warn('Rigenerazione PDF non riuscita, uso quello salvato:', err);
    return voce.pdf || null;
  }
}

async function spedisci(voce) {
  const rotte = { ...(ROTTE[voce.tipo] || {}), ...(voce.rotte || {}) };
  if (!rotte.complete) {
    throw Object.assign(new Error(`Tipo sconosciuto: ${voce.tipo}`), { definitivo: true });
  }

  // 1) la lavorazione. I rapportini gia' assegnati la hanno gia', con il
  // suo codice interno: questo passo li salta.
  if (!voce.fatto.init && rotte.init) {
    const risposta = await posta(rotte.init, {
      ...voce.datiIniziali,
      // stabile per tutta la vita della voce in coda: e' cio' che permette
      // al server di riconoscere un reinvio invece di creare un doppione
      clientRequestId: voce.id,
    });

    voce.workOrderId = risposta?.workOrder?._id || voce.workOrderId;

    // il codice interno esiste solo adesso: va scritto nei dati prima di
    // chiudere il rapportino e prima di rigenerare il PDF per la mail
    const codice = risposta?.workOrder?.internalCode || '';
    if (codice) {
      voce.internalCode = codice;
      if (voce.datiCompletamento?.formData) voce.datiCompletamento.formData.codiceInterno = codice;
      if (voce.datiCompletamento?.summary) voce.datiCompletamento.summary.codiceInterno = codice;
    }

    voce.fatto.init = true;
    await scrivi(ARCHIVI.OUTBOX, voce);
  }

  // 2) la chiusura del rapportino
  if (!voce.fatto.complete) {
    await posta(rotte.complete, {
      ...voce.datiCompletamento,
      workOrderId: voce.workOrderId || voce.datiCompletamento?.workOrderId,
    });

    voce.fatto.complete = true;
    await scrivi(ARCHIVI.OUTBOX, voce);
  }

  // 3) il gas usato e recuperato.
  //
  // Va fatto adesso e non alla compilazione: il calcolo legge le giacenze,
  // le modifica e le riscrive, quindi il risultato dipende da com'erano al
  // momento dell'invio. Un errore qui non blocca il rapportino, che e' gia'
  // registrato: viene solo segnalato.
  if (!voce.fatto.gas) {
    if (voce.gas) {
      try {
        await registraGas({
          ...voce.gas,
          contesto: voce.tipo === TIPI.VIT ? 'un intervento' : 'una manutenzione',
        });
      } catch (err) {
        console.error('Registrazione gas non riuscita:', err);
        voce.erroreGas = err?.message || String(err);
      }
    }

    voce.fatto.gas = true;
    await scrivi(ARCHIVI.OUTBOX, voce);
  }

  // 4) la mail col PDF.
  //
  // Il PDF viene rigenerato adesso, non riusato quello scaricato sul
  // posto: quello e' stato prodotto quando il codice interno non esisteva
  // ancora, e la copia che finisce in archivio e in mail deve portarlo.
  if (!voce.fatto.report) {
    const pdf = await pdfPerLaMail(voce);

    if (pdf) {
      await posta(rotte.report, {
        formData: voce.datiCompletamento?.formData || {},
        pdfBase64: await blobInBase64(pdf),
      });
    }

    voce.fatto.report = true;
    await scrivi(ARCHIVI.OUTBOX, voce);
  }
}

let inCorso = false;

/**
 * Svuota la coda in ordine di compilazione, una voce alla volta: il server
 * assegna i codici interni nell'ordine in cui riceve i rapportini, quindi
 * spedirli in disordine darebbe numeri in disordine.
 */
export async function sincronizza() {
  if (inCorso || !disponibile()) return { inviati: 0, rimasti: await contaInAttesa() };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { inviati: 0, rimasti: await contaInAttesa(), offline: true };
  }

  inCorso = true;
  let inviati = 0;

  try {
    const voci = (await elencoInAttesa()).sort((a, b) =>
      String(a.creatoIl).localeCompare(String(b.creatoIl))
    );

    for (const voce of voci) {
      try {
        await spedisci(voce);
        await elimina(ARCHIVI.OUTBOX, voce.id);
        inviati += 1;
      } catch (err) {
        voce.tentativi = (voce.tentativi || 0) + 1;
        voce.ultimoErrore = err?.message || String(err);
        voce.stato = err?.definitivo || voce.tentativi >= MAX_TENTATIVI ? 'errore' : 'in_attesa';

        await scrivi(ARCHIVI.OUTBOX, voce);

        // se e' caduta la rete inutile insistere sulle altre
        if (!err?.definitivo && typeof navigator !== 'undefined' && !navigator.onLine) break;
      }
    }
  } finally {
    inCorso = false;
    await avvisa();
  }

  return { inviati, rimasti: await contaInAttesa() };
}

/**
 * Riprova a spedire quando torna la rete e all'avvio dell'app.
 *
 * Non usa la Background Sync API: su iOS non esiste, e i tecnici hanno
 * anche iPhone. Qui basta che l'app sia aperta.
 */
export function avviaSincronizzazioneAutomatica() {
  if (typeof window === 'undefined') return () => {};

  const alRitornoDellaRete = () => {
    sincronizza().catch(() => {});
  };

  window.addEventListener('online', alRitornoDellaRete);

  const allApertura = () => {
    if (document.visibilityState === 'visible') alRitornoDellaRete();
  };
  document.addEventListener('visibilitychange', allApertura);

  alRitornoDellaRete();

  return () => {
    window.removeEventListener('online', alRitornoDellaRete);
    document.removeEventListener('visibilitychange', allApertura);
  };
}
